/* eslint-disable max-lines */
import { action, computed, extendObservable, has, reaction, runInAction, set } from 'mobx';
import moment from 'moment';

import AccessLevelService, { verifiedRoomModerator } from 'services/AccessLevelService';
import BroadcastNotificationsService from 'services/BroadcastNotificationsService';
import ChatService from 'services/ChatService';
import { getActiveLanguageCode, translate } from 'services/i18nService';
import FirebaseService from 'services/FirebaseService';
import createMessagingService from 'services/MessagingService/MessagingService';
import {
  CHAT_MESSAGE_RECEIVED,
  CHAT_TRANSLATION_SETTINGS_UPDATED,
  CLEAR_GAME_RESULT,
  FIREBASE_UNAVAILABLE,
  FIREBASE_NEW_MAIN_CHAT_MESSAGE,
  GET_ACTIVE_GAME_INFO_TRIGGER,
  LOAD_INITIAL_CHAT_MESSAGES,
  LOGIN_SUCCESS,
  MODERATOR_PRIVILEGE_CHANGED,
  RESET_PRIVATE_ROOM,
  STREAM_STARTED,
  STREAM_STARTED_TIPPING_MUTED,
  UPDATE_ANONYMOUS_TIPPING_HASH,
  UPDATE_CURRENT_GAME,
  UPDATE_WEB_PRESENCE_CHAT_STATUS,
  USER_LOGGED_OUT,
  INITIAL_MESSAGE_TRANSLATION,
  PUBLIC_MESSAGE_SENT,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import TrackingService from 'services/TrackingService';
import BroadcasterPrivateShowStore from 'stores/BroadcasterPrivateShowStore';
import chatStoreInitialState from 'stores/ChatStoreInitialState';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import GiphyStore from 'stores/GiphyStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import TippingStore from 'stores/TippingStore';
import { translationLanguages } from 'enum/chatSettings';
import {
  NOTIFICATION_TYPE,
  MESSAGE_TYPES,
} from 'enum/messages';
import { SENT_CHAT_MESSAGE_STATUSES } from 'enum/helpers';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import {
  GAME_OPERATIONS,
  GAMES,
  GAMES_STATUS,
  TabPanelType,
} from 'utils/consts';
import { noFilterForCAM4Staff } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';
import chatBotsStore from 'services/chatBotsService/chatBotsStore';
import { MINUTE_AS_MILLISECONDS } from 'enum/timeFormat';


const MAX_FOREIGN_LANGUAGE_MESSAGES = 5;
const NOTIFICATION = 20;
const isBroadcastWindow = location.pathname === '/broadcast';
const EVENT_NOTIFICATION_INTERVAL = 5 * MINUTE_AS_MILLISECONDS;

class ChatStore {
  constructor() {
    extendObservable(this, chatStoreInitialState);
    this.messagingService = createMessagingService();
    this.messagingService.on(LOGIN_SUCCESS, this._handleLogIn);
    this.messagingService.on(STREAM_STARTED, this._handleStreamStarted);
    this.messagingService.on(STREAM_STARTED_TIPPING_MUTED, this.#unmuteTippingSounds);
    this.messagingService.on(USER_LOGGED_OUT, this._handleLogout);
    this.messagingService.on(MODERATOR_PRIVILEGE_CHANGED, this._handleModNotification);
    this.messagingService.on(FIREBASE_UNAVAILABLE, this._handleFirebaseUnavailable);
    this.messagingService.on(FIREBASE_NEW_MAIN_CHAT_MESSAGE, this._handleMainChatLight);
    this.startEventNotificationSystem();
  }

  destructor() {
    this.messagingService.off(LOGIN_SUCCESS, this._handleLogIn);
    this.messagingService.off(STREAM_STARTED, this._handleStreamStarted);
    this.messagingService.off(STREAM_STARTED_TIPPING_MUTED, this.#unmuteTippingSounds);
    this.messagingService.off(USER_LOGGED_OUT, this._handleLogout);
    this.messagingService.off(MODERATOR_PRIVILEGE_CHANGED, this._handleModNotification);
    this.messagingService.off(FIREBASE_UNAVAILABLE, this._handleFirebaseUnavailable);
    this.messagingService.off(FIREBASE_NEW_MAIN_CHAT_MESSAGE, this._handleMainChatLight);
    this.stopEventNotificationSystem();
  }

  getEventNotification = () => {
    return {
      type: NOTIFICATION_TYPE.EVENT_NOTIFICATION,
      hash: Date.now(),
      messageKey: 'eventNotif',
    };
  };

  addEventNotification() {
    if (!ProfileStore.realityShow) {
      return;
    }

    const last3 = -3;
    const last3Messages = this.messages.slice(last3);
    const alreadyHasNotification = last3Messages.find(
      ({ type }) => Boolean(type === NOTIFICATION_TYPE.EVENT_NOTIFICATION)
    );

    if (alreadyHasNotification) {
      return;
    }

    this.messages.push(this.getEventNotification());
  }

  startEventNotificationSystem() {
    this.stopEventNotificationSystem();

    this.eventNotificationInterval = setInterval(() => {
      this.addEventNotification();
    }, EVENT_NOTIFICATION_INTERVAL);
  }

  stopEventNotificationSystem() {
    clearInterval(this.eventNotificationInterval);
  }

  @action setBroadcasterChatTab = (value) => {
    this.activeBroadcasterChatTab = value;
  };

  @action _handleLogIn = () => {
    if (this.campaignMsg) {
      this.removeMessagesByType(NOTIFICATION_TYPE.CAMPAIGN_CTA);
    }
  };

  @action _handleStreamStarted = ({ muted }) => {
    BroadcastNotificationsService.isTippingSoundMuted = muted;
    this.scrollToBottom = !this.scrollToBottom;
  };

  #unmuteTippingSounds = () => {
    BroadcastNotificationsService.isTippingSoundMuted = false;
  };

  @action togglePrivateUnreadMessagesIndicator = (value) => {
    this.hasPrivateUnreadMessages = value;
  };

  @action togglePrivateMessages = (flag) => {
    this.isPrivateMessageOpen = flag;
    if (this.hasPrivateUnreadMessages && flag) {
      this.togglePrivateUnreadMessagesIndicator(false);
    }
    if (!flag) {
      this.hasUnreadMessages = false;
    }
  };

  @action toggleUnreadMessages = (flag) => {
    this.hasUnreadMessages = flag;
  };

  @action toggleGsUnreadMessages = (flag) => {
    this.hasUnreadGsMessages = flag;
  };

  @action toggleGsChat = (flag) => {
    this.gsChatOpen = flag;
    if (flag) {
      this.hasUnreadGsMessages = false;
    }
  };

  @computed get hasUnreadUser2UserMessages() {
    const chatTabsList = Object.keys(this.privateUserChats).filter(
      (key) => key.length > 0 && key.toLowerCase() !== ProfileStore.profileName.toLowerCase()
    );
    let hasUnreadMsg = false;
    chatTabsList.forEach((key) => {
      const lastMsgNotFromLoggedInUser =
        this.privateUserChats[key]?.messages.length > 0 &&
        this.privateUserChats[key]?.messages[this.privateUserChats[key].messages.length - 1].senderOriginalUsername !==
          UserStore.user.username;
      if (
        (!this.activeReadConversations[key] ||
          this.privateUserChats[key].lastMessageTimestamp > this.activeReadConversations[key].lastReadTimestamp) &&
        lastMsgNotFromLoggedInUser
      ) {
        hasUnreadMsg = true;
      }
    });

    return hasUnreadMsg;
  }

  @action handleChatInputError = (error) => {
    this.isChatInputErrorVisible = error;
  };

  @action toggleChatShown = () => {
    this.isChatShown = !this.isChatShown;
  };

  @action resetChatShown = () => {
    this.isChatShown = true;
    this.isPrivateShowChatShown = true;
  };

  @action toggleInteractionShown = () => {
    this.isInteractionShown = !this.isInteractionShown;
  };

  @action togglePrivateShowChatShown = () => {
    this.isPrivateShowChatShown = !this.isPrivateShowChatShown;
  };

  @action openMobileKeyboard = () => {
    this.isMobileKeyboardActive = true;
  };

  @action closeMobileKeyboard = () => {
    this.isMobileKeyboardActive = false;
  };

  @action openEmojiPicker = () => {
    this.isEmojiPickerVisible = true;
  };

  @action onCloseEmojiPicker = () => {
    this.isEmojiPickerVisible = false;
  };

  @action handleChatInput = (value) => {
    this.chatInputValue = value;
  };

  @action resizeWindowHeight = (event) => {
    this.windowHeight = event.target.innerHeight;
  };

  @action setInitialWindowHeight = (height) => {
    this.initialWindowHeight = height;
  };

  @action setChatInputError = (errorMsg) => {
    this.chatInputErrorMessage = errorMsg;
  };

  @action sendMainChatMessage = (username, accessHash, roomname, message, recipient, isGif, gifUrlAnalytics) => {
    if (!message) {
      return;
    }
    if (this.isPrivateMessageOpen || recipient) {
      if (!message.trim()) {
        this.handleChatInputError(true);
        this.setChatInputError(SENT_CHAT_MESSAGE_STATUSES.EMPTY_MESSAGE);
      } else {
        set(this.activeReadConversations, { [recipient]: { lastReadTimestamp: Date.now() } });
        ChatService.sendPrivateChatMessage(
          username,
          accessHash,
          roomname,
          message,
          recipient,
          isGif,
          gifUrlAnalytics
        ).then((res) => {
          this.scrollToBottom = !this.scrollToBottom;
          const isErrorVisible = [
            SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MESSAGE_SPAM,
            SENT_CHAT_MESSAGE_STATUSES.THROTTLER_TOO_MANY_MESSAGES,
            SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MUST_WAIT,
            SENT_CHAT_MESSAGE_STATUSES.SPAM_DETECTED,
            SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT,
            SENT_CHAT_MESSAGE_STATUSES.USER_LANGUAGE_NOT_ALLOWED_TO_CHAT,
            SENT_CHAT_MESSAGE_STATUSES.ONLY_FRIENDS_FAVORITES_ALLOWED_TO_CHAT,
            SENT_CHAT_MESSAGE_STATUSES.GENDER_NOT_ALLOWED_TO_CHAT,
            SENT_CHAT_MESSAGE_STATUSES.GIPHY_SENT_TOO_SOON,
            SENT_CHAT_MESSAGE_STATUSES.COULD_NOT_PARSE_GIPHY_URL,
          ].includes(res.data.status);
          if (isErrorVisible) {
            this.handleChatInputError(true);
            this.setChatInputError(res.data.status);
          }
        });
      }
    } else if (!message.trim()) {
      this.handleChatInputError(true);
      this.setChatInputError(SENT_CHAT_MESSAGE_STATUSES.EMPTY_MESSAGE);
    } else {
      ChatService.sendMainChatMessage(username, accessHash, roomname, message, isGif, gifUrlAnalytics).then((res) => {
        this.messagingService.emit(PUBLIC_MESSAGE_SENT, { username, roomname, message, status: res.data.status });
        this.scrollToBottom = !this.scrollToBottom;
        const isErrorVisible = [
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MESSAGE_SPAM,
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_TOO_MANY_MESSAGES,
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MUST_WAIT,
          SENT_CHAT_MESSAGE_STATUSES.SPAM_DETECTED,
          SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT,
          SENT_CHAT_MESSAGE_STATUSES.USER_LANGUAGE_NOT_ALLOWED_TO_CHAT,
          SENT_CHAT_MESSAGE_STATUSES.ONLY_FRIENDS_FAVORITES_ALLOWED_TO_CHAT,
          SENT_CHAT_MESSAGE_STATUSES.GENDER_NOT_ALLOWED_TO_CHAT,
          SENT_CHAT_MESSAGE_STATUSES.GIPHY_SENT_TOO_SOON,
          SENT_CHAT_MESSAGE_STATUSES.NOT_IN_THE_ROOM,
        ].includes(res.data.status);
        if (res.data.status === SENT_CHAT_MESSAGE_STATUSES.NOT_ALLOWED_TO_CHAT_WITHOUT_TOKENS) {
          ModalService.showModal(ModalService.MODAL_KEYS.TOKEN_HOLDERS_ALLOWED_MODAL);
        }
        if (res.data.status === SENT_CHAT_MESSAGE_STATUSES.COULD_NOT_PARSE_GIPHY_URL) {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('account.errorGeneric'),
            subtype: NotificationService.subtype.ERROR,
          });
        } else {
          if (isErrorVisible) {
            this.handleChatInputError(true);
            this.setChatInputError(res.data.status);
            this.messagingService.emit(UPDATE_WEB_PRESENCE_CHAT_STATUS, false);
          }
          if (res.data.status === SENT_CHAT_MESSAGE_STATUSES.GUEST_OR_SILENCED_USERS_NOT_ALLOWED_TO_CHAT) {
            const silencedMessage = {
              message,
              isSilenced: true,
              senderOriginalUsername: username,
              type: 1,
              timestamp: Date.now(),
            };
            this.messages.push(silencedMessage);
          }
        }
      });
    }
    this.chatInputValue = '';
  };

  @action setChatAuthentication(isChatAuthenticated) {
    this.isChatAuthenticated = isChatAuthenticated;
  }

  @action resetInitialMessageState = () => {
    this.initialMessagesSet = false;
  };

  @action deleteLiveTouchNotif = () => {
    this.messages = this.messages.filter((msg) => msg.type !== NOTIFICATION_TYPE.LIVE_TOUCH);
  };

  @action populateInitialMessageHistory(parsedHistoryMessages, username) {
    const cachedMessages = new Set(this.messages.map((d) => d.messageKey));
    let messages = [
      ...this.messages,
      ...parsedHistoryMessages.filter((d) => !cachedMessages.has(d.messageKey)),
    ].sort((a, b) => a.timestamp - b.timestamp);
    if (this.chatSettings.enableFilters) {
      if (this.chatSettings.allowGender) {
        if (this.chatSettings.selectedGender === 'shemale') {
          messages = messages.filter(
            (msg) =>
              noFilterForCAM4Staff(msg.senderAccessLevel, ProfileStore.profileName, msg.senderOriginalUsername) ||
              username === msg.senderOriginalUsername ||
              msg.gender === 'transgender' ||
              msg.type !== NOTIFICATION_TYPE.MESSAGE
          );
        } else {
          messages = messages.filter(
            (msg) =>
              noFilterForCAM4Staff(msg.senderAccessLevel, ProfileStore.profileName, msg.senderOriginalUsername) ||
              username === msg.senderOriginalUsername ||
              msg.gender === this.chatSettings.selectedGender ||
              msg.type !== NOTIFICATION_TYPE.MESSAGE
          );
        }
      }
      if (this.chatSettings.allowMyLanguage) {
        messages = messages.filter(
          (msg) =>
            noFilterForCAM4Staff(msg.senderAccessLevel, ProfileStore.profileName, msg.senderOriginalUsername) ||
            username === msg.senderOriginalUsername ||
            msg.language === (ProfileStore.loggedInUserInfo.mainLanguage || window.LOCALE) ||
            ProfileStore.loggedInUserInfo?.secondaryLanguages?.includes?.(msg.language) ||
            msg.type !== NOTIFICATION_TYPE.MESSAGE
        );
      }
    }
    if (
      ProfileStore.silenceList?.totalUsersCount > 0 &&
      !FirebaseStore.isLoggedInUserRoomModerator(username) &&
      FirebaseStore.isLoggedInUserRoomModerator(username) !== undefined
    ) {
      messages = messages.filter(
        (msg) =>
          noFilterForCAM4Staff(msg.senderAccessLevel, ProfileStore.profileName, msg.senderOriginalUsername) ||
          !ProfileStore.silencedListUsernames.includes(msg.senderOriginalUsername)
      );
    }
    if (messages.length) {
      this.lastChatMsgKey = messages[messages.length - 1].messageKey;
      this.lastChatMsgTimeStamp = messages[messages.length - 1].timestamp;
    }
    this.initialMessagesSet = true;
    this.messages = messages;
    this.addEventNotification();
    this.hasUnreadMessages = true;
    this.messagingService.emit(LOAD_INITIAL_CHAT_MESSAGES, {
      type: 'public',
      messages: this.messages,
      settings: this.translationSettings,
    });
  }

  @action populateInitialGroupShowMessages(parsedHistoryMessages) {
    this.initialMessagesSet = true;
    this.groupShowMessages = parsedHistoryMessages;
    this.messagingService.emit(LOAD_INITIAL_CHAT_MESSAGES, {
      type: 'groupShow',
      messages: this.groupShowMessages,
      settings: this.translationSettings,
    });
  }

  @action populateInitialPrivateMessageHistory(parsedHistoryMessages) {
    this.initialMessagesSet = true;
    const newMessages = parsedHistoryMessages.filter(
      (msg) => !this.privateMessages.find((m) => m.messageKey === msg.messageKey)
    ).map((msg) => extendObservable(msg, { translatedMessage: undefined }));
    const addToBegining = newMessages[0]?.timestamp <
      this.privateMessages[this.privateMessages.length - 1]?.timestamp;
    if (addToBegining) {
      this.privateMessages.unshift(...newMessages);
    } else {
      this.privateMessages.push(...newMessages);
    }
    this.translatePrivateMsgs();
    const lastMessageTimeStamp =
      this.privateUserChats?.[ProfileStore.performerDetails.username]?.lastMessageTimestamp;
    const lastMessageWithBroadcaster =
      this.privateUserChats?.[ProfileStore.performerDetails.username]?.messages[
        this.privateUserChats[ProfileStore.performerDetails.username].messages.length - 1
      ];
    const hasNewUnreadMessages =
      lastMessageTimeStamp > this.lastPrivateMessageTimestamp &&
      lastMessageWithBroadcaster.senderOriginalUsername !== UserStore.user.username;
    const setUnreadInPublicShowChat =
      (!this.isPrivateMessageOpen || this.profileChatTabValue !== 1) && hasNewUnreadMessages;
    const setUnreadInPrivateShowChat = !this.isPrivateShowChatShown && hasNewUnreadMessages;
    if (setUnreadInPublicShowChat || setUnreadInPrivateShowChat) {
      this.lastPrivateMessageTimestamp = lastMessageTimeStamp;
      this.togglePrivateUnreadMessagesIndicator(true);
    }
    this.messagingService.emit(LOAD_INITIAL_CHAT_MESSAGES, {
      type: 'private',
      messages: this.privateMessages,
      settings: this.translationSettings,
    });
  }

  @action addMessage = (parsedLightMessage, isBroadcasterChat, cb) => {
    const translationsActive = this.translationSettings.active;
    const shouldTranslateBroadcasterMessage =
      isBroadcasterChat &&
      parsedLightMessage?.senderOriginalUsername !== UserStore?.user?.username;
    const shouldTranslateProfilesMessage =
      !isBroadcasterChat &&
      (this.isGold || window?.abt?.chat?.enforceTranslations);

    if (
      translationsActive &&
      (shouldTranslateBroadcasterMessage || shouldTranslateProfilesMessage) &&
      parsedLightMessage.message !== '' &&
      parsedLightMessage.type === NOTIFICATION_TYPE.MESSAGE
    ) {
      ChatService.translateMsg(parsedLightMessage, this.translationSettings)
        .then((res) => {
          const isSame = parsedLightMessage.message === res.translatedText;
          if (!isSame) {
            parsedLightMessage.translatedMessage = res.translatedText;
          }
          parsedLightMessage.detectedSourceLanguage = res.detectedSourceLanguage;
          this.messages = [...this.messages, parsedLightMessage];
          this.hasUnreadMessages = true;

          if (cb && typeof cb === 'function') {
            cb();
          }
        })
        .catch(() => {
          this.messages = [...this.messages, parsedLightMessage];
          this.hasUnreadMessages = true;

          if (cb && typeof cb === 'function') {
            cb();
          }
        });
    } else {
      this.messages = [...this.messages, parsedLightMessage];
      this.hasUnreadMessages = true;

      if (cb && typeof cb === 'function') {
        cb();
      }
    }
    this.messagingService.emit(CHAT_MESSAGE_RECEIVED, {
      type: 'public',
      data: parsedLightMessage,
      settings: this.translationSettings,
    });
  };

  @action removeMessagesByType(type) {
    const messagesArray = this.messages.filter((msg) => msg.type !== type);
    this.messages = messagesArray;
  }

  @computed get isBwr() {
    return location.pathname === '/broadcast';
  }

  @action addGroupShowMessage = (parsedLightMessage) => {
    const notGroupShowChat = this.activeBroadcasterChatTab !== TabPanelType.specialShowChat;
    const shouldToggleUnreadFlag =
      (parsedLightMessage.senderOriginalUsername !== UserStore.user.username) && (
        (!this.gsChatOpen && this.profileChatTabValue !== 1 && !this.isBwr) ||
        !this.isScrollAtBottom ||
        (notGroupShowChat && this.isBwr)
      );

    if (
      (this.isGold || isBroadcastWindow || window?.abt?.chat?.enforceTranslations) &&
      parsedLightMessage.type === NOTIFICATION_TYPE.MESSAGE &&
      parsedLightMessage.translatedMessage === undefined &&
      parsedLightMessage.message !== ''
    ) {
      ChatService.translateMsg(parsedLightMessage, this.translationSettings)
        .then((res) => {
          const isSame = parsedLightMessage.message === res.translatedText;
          if (!isSame) {
            parsedLightMessage.translatedMessage = res.translatedText;
          }
          this.groupShowMessages = [...this.groupShowMessages, parsedLightMessage];
          if (shouldToggleUnreadFlag) {
            this.hasUnreadGsMessages = true;
          }
        })
        .catch(() => {
          this.groupShowMessages = [...this.groupShowMessages, parsedLightMessage];
        });
    } else {
      this.groupShowMessages = [...this.groupShowMessages, parsedLightMessage];
      if (shouldToggleUnreadFlag) {
        this.hasUnreadGsMessages = true;
      }
    }
    this.messagingService.emit(CHAT_MESSAGE_RECEIVED, {
      type: 'groupShow',
      data: parsedLightMessage,
      settings: this.translationSettings,
    });
  };

  @action openGoalDrawer = () => {
    this.goalDrawer = true;
  };

  @action closeGoalDrawer = () => {
    this.goalDrawer = false;
  };

  @action addPrivateMessage(parsedLightMessage) {
    this.privateMessages = [...this.privateMessages, parsedLightMessage];
  }

  @action deletePrivateMessages = () => {
    this.privateMessages = [];
    this.isPrivateMessageOpen = false;
  };

  @action stateToInitialValues() {
    this.messages = [];
    this.initialMessagesSet = false;
  }

  removeChatListeners = (roomName, username) => {
    if (FirebaseService.FirebaseMain) {
      if (username) {
        const privateChatRef = FirebaseService.FirebaseMain.child(
          `chatRooms/${roomName}/privateMessages/${username.toLowerCase()}`
        );
        if (privateChatRef) {
          privateChatRef.off();
        }
      }
      const mainChatLightRef = FirebaseService.FirebaseMain.child(`c/${roomName.toLowerCase()}`);
      const groupShowChatRef = FirebaseService.FirebaseMain.child(`chatRooms/${roomName}/groupShowMessages`);
      if (mainChatLightRef) {
        mainChatLightRef.off();
      }
      if (groupShowChatRef) {
        groupShowChatRef.off();
      }
    }
  };


  checkForLovenseNotifications = (parsedLightMessage) => {
    if (parsedLightMessage.notificationExtra) {
      const notificationExtra = JSON.parse(parsedLightMessage.notificationExtra);
      if (
        parsedLightMessage.type === NOTIFICATION_TYPE.SEND_TIP ||
        (parsedLightMessage.type === NOTIFICATION_TYPE.GAME &&
          notificationExtra?.s !== GAMES_STATUS.IDLE &&
          Object.keys(notificationExtra).length > 0)
      ) {
        const activatedGame = GamesStore.activeGamesInfo.find((game) => game.name === parsedLightMessage.gameName);
        this.tipNotifications.push(
          Object.assign(parsedLightMessage, { price: activatedGame?.details?.p })
        );
      }
    }
  };

  @action watchMainChat = (roomName, isGold, username, chatHistory) => {
    const useWebchatChatHistory = UserStore.hasCF(allUserGroups.USE_WEBCHAT_CHAT_HISTORY) && chatHistory;
    if (useWebchatChatHistory) {
      this.getLastMessages('chatMessages', chatHistory.messages, username)
        .then(() => this.listenForMainChatMessages(roomName, isGold, username));

      return;
    }

    ChatService.watchMainChat(roomName, (snapshot) =>
      this.getLastMsgs(snapshot, username)
        .then(() => this.listenForMainChatMessages(roomName, isGold, username))
    );
  };

  @action listenForMainChatMessages = (roomName, isGold, username) => {
    this.isGold = isGold;
    const isBroadcasterChat = username ? roomName.toLowerCase() === username.toLowerCase() : false;
    const mainChatLightRef = FirebaseService.FirebaseMain.child(`c/${roomName.toLowerCase()}`);
    if (isGold || isBroadcasterChat || window?.abt?.chat?.enforceTranslations) {
      this.getTranslationSettings(username);
    }
    mainChatLightRef.on('value', (snapshot) => {
      this._handleMainChatLight({ snapshot, roomName, username, isBroadcasterChat });
    });
  };

  // eslint-disable-next-line complexity, no-shadow
  _handleMainChatLight = async({ snapshot, roomName, username, isBroadcasterChat }) => {
    const message = snapshot.val();
    const { sendTippingBotMessage } = chatBotsStore;

    if (message) {
      if (Object.hasOwn(message, 'delete')) {
        // deleting banned user msgs
        const filteredMessages = this.messages.filter((msg) => Number(msg.timestamp) !== Number(message.tm)
         || msg.type !== NOTIFICATION_TYPE.MESSAGE);
        this.messages = filteredMessages;
      } else {
        let parsedLightMessage = {};
        if (message.gid && message.gid !== '') {
          parsedLightMessage = await GiphyStore.getSingleGif(message, '', FirebaseStore.kiirooRoom, true);
        } else {
          parsedLightMessage = ChatService.parseLightMessage({
            ...snapshot.val(),
            isLiveTouchOn: FirebaseStore.kiirooRoom,
          });
        }
        let info = {};
        if (parsedLightMessage.type === NOTIFICATION_TYPE.ROOM_PASSWORD_CHANGED) {
          this.messagingService.emit(RESET_PRIVATE_ROOM);
        }
        if (
          this.isBwr &&
          parsedLightMessage.type === NOTIFICATION_TYPE.BOOST_STARTED &&
          roomName.toLowerCase() === username?.toLowerCase()
        ) {
          BroadcastNotificationsService.handleBoostNotificationSounds(parsedLightMessage);
        }

        if (this.isBwr && parsedLightMessage.type === NOTIFICATION_TYPE.GAME) {
          if (parsedLightMessage.gameOperation === GAME_OPERATIONS.START) {
            GamesStore.startAutoNotifications(parsedLightMessage);
          } else if (parsedLightMessage.gameOperation === GAME_OPERATIONS.STOP) {
            GamesStore.stopAutoNotifications(parsedLightMessage);
          }
        }
        if (
          this.isBwr &&
          (parsedLightMessage.isTippingMessage || parsedLightMessage.gameName === GAMES.TIP_MENU) &&
          roomName.toLowerCase() === username?.toLowerCase()
        ) {
          BroadcastNotificationsService.handleTippingSound(parsedLightMessage);
          TippingStore.getPerformerSessionTippingInfo(UserStore.user, FirebaseStore.tippingSessionId);
          sendTippingBotMessage(parsedLightMessage, roomName);
        }
        if ([
          GAMES.SPIN_THE_WHEEL,
          GAMES.TOKEN_SQUARES,
          GAMES.ROLL_THE_DICE,
        ].includes(parsedLightMessage.gameName)) {
          if (parsedLightMessage.notificationExtra) {
            const alreadyShown = this.messages.find(
              (msg) => msg.timestamp === parseInt(parsedLightMessage.timestamp, 10)
            );
            if (JSON.parse(parsedLightMessage.notificationExtra).o && !alreadyShown) {
              this.messagingService.emit(GET_ACTIVE_GAME_INFO_TRIGGER, {
                roomName,
                token: FirebaseStore.apiToken,
              });
            }
            info = JSON.parse(parsedLightMessage.notificationExtra);
            if (this.isBwr && roomName?.toLowerCase() === username?.toLowerCase()) {
              BroadcastNotificationsService.handleGameNotificationSounds(parsedLightMessage);
            }
          }
          const infoNotEmpty = Object.keys(info).length;
          if (parsedLightMessage.senderOriginalUsername && infoNotEmpty) {
            info.prizeWinner = parsedLightMessage.senderOriginalUsername;
          }
          if (parsedLightMessage.gameName && infoNotEmpty) {
            info.gameName = parsedLightMessage.gameName;
          }
          if (parsedLightMessage.type === NOTIFICATION) {
            this.messagingService.emit(UPDATE_CURRENT_GAME, info);
            if (!info.gameName && this.profileChatTabValue === 4) {
              this.setProfileChatTabValue(0);
              this.messagingService.emit(CLEAR_GAME_RESULT, {});
            } else if (info.gameName && info.s === GAMES_STATUS.IDLE) {
              this.messagingService.emit(CLEAR_GAME_RESULT, {});
            }
          }
        }
        if (parsedLightMessage.gameName === GAMES.TIP_MENU) {
          const alreadyShown = this.messages.find(
            (msg) => msg.timestamp === parseInt(parsedLightMessage.timestamp, 10)
          );
          if (!alreadyShown) {
            this.messagingService.emit(GET_ACTIVE_GAME_INFO_TRIGGER, { roomName, token: FirebaseStore.apiToken });
          }
        }
        // skipping duplicates
        const duplicate = this.messages.find(
          (msg) =>
            msg.timestamp === parseInt(parsedLightMessage.timestamp, 10)
        );
        // preparing variables for code readability
        const isShemaleSelectedAndIsMsgGenderTrans =
          this.chatSettings.selectedGender === 'shemale' && parsedLightMessage.gender !== 'transgender';
        const isAnyOtherGenderSelected =
          this.chatSettings.selectedGender !== 'shemale' &&
          parsedLightMessage.gender !== this.chatSettings.selectedGender;
        const isChatMsgAndIsChatFiltersEnabled =
          parsedLightMessage.type === NOTIFICATION_TYPE.MESSAGE && this.chatSettings.enableFilters &&
          this.chatSettings.allowGender;
        const isChatMsgAndIsLanguageFilterEnabled =
          parsedLightMessage.type === NOTIFICATION_TYPE.MESSAGE && this.chatSettings.enableFilters &&
          this.chatSettings.allowMyLanguage;
        const isChatMsgLanguageDifferentFromChatFilterLanguage =
            parsedLightMessage.language === (ProfileStore.loggedInUserInfo.mainLanguage || window.LOCALE) ||
          ProfileStore.loggedInUserInfo?.secondaryLanguages?.includes?.(parsedLightMessage.language);
        const isChatMsgFromSilencedUser =
          ProfileStore.silenceList.totalUsersCount > 0 &&
          ProfileStore.silencedListUsernames?.includes(parsedLightMessage.senderOriginalUsername);
        // filtering messages if chat filters are enabled
        const chatSettingsFilterGender =
          isChatMsgAndIsChatFiltersEnabled && (isAnyOtherGenderSelected || isShemaleSelectedAndIsMsgGenderTrans);
        const chatSettingsFilterLanguage =
          isChatMsgAndIsLanguageFilterEnabled && !isChatMsgLanguageDifferentFromChatFilterLanguage;
        // no filters for user that sent msg and for cam4 staff
        const noFiltersForSender =
          username?.toLowerCase() ===
          parsedLightMessage?.senderOriginalUsername?.toLowerCase();
        const cam4Staff = noFilterForCAM4Staff(
          parsedLightMessage.senderAccessLevel,
          ProfileStore.profileName,
          parsedLightMessage.senderOriginalUsername
        );
        const filterMessages =
          (chatSettingsFilterGender || chatSettingsFilterLanguage) && !noFiltersForSender && !cam4Staff;
        // filtering silenced users
        const silenceFilters = isChatMsgFromSilencedUser && !cam4Staff;

        if (
          this.lastChatMsgTimeStamp === parsedLightMessage.timestamp ||
          duplicate ||
          filterMessages ||
          silenceFilters
        ) {
          return;
        }
        this.lastChatMsgKey = parsedLightMessage.messageKey;
        this.lastChatMsgTimeStamp = parsedLightMessage.timestamp;
        this.addMessage(parsedLightMessage, isBroadcasterChat, () => {
          if (isBroadcasterChat) {
            this.countForeignLanguagesAndSetFlag(roomName);
          }
        });
        if (this.isBwr) {
          this.checkForLovenseNotifications(parsedLightMessage);
        }
      }
    }
  };

  @action countForeignLanguagesAndSetFlag = (roomName) => {
    this.foreignLanguageMessagesCount(roomName).then((count) => {
      if (count >= MAX_FOREIGN_LANGUAGE_MESSAGES) {
        this.isForeignLanguagesTresholdReached = true;
      }
    });
  };

  foreignLanguageMessagesCount = async(roomName) => {
    const translationPromises = this.messages
      .filter(
        (message) =>
          this._isViewerNonEmptyTextMessage(message, roomName) &&
          message?.detectedSourceLanguage === undefined &&
          message.senderOriginalUsername !== UserStore.user.username
      )
      .map(this._detectLanguageAndSaveMessage);

    const restMessages = this.messages.filter(
      (message) => this._isViewerNonEmptyTextMessage(message, roomName) && message?.detectedSourceLanguage !== undefined
    );

    const translatedMessages = await Promise.all(translationPromises);
    const activeLanguageCode = getActiveLanguageCode();

    return [...translatedMessages, ...restMessages].filter(
      (message) => message?.detectedSourceLanguage !== activeLanguageCode
    ).length;
  };

  _isViewerNonEmptyTextMessage = (message, roomName) =>
    message?.senderOriginalUsername !== roomName.toLowerCase() &&
      message?.type === NOTIFICATION_TYPE.MESSAGE &&
      message?.message !== '';

  _detectLanguageAndSaveMessage = async(msg) => {
    const translatedMsg = await ChatService.translateMsg(msg, this.translationSettings);
    this._saveTranslatedMessage(msg, translatedMsg);

    return msg;
  };

  @action watchPrivateChat = (roomName, username) => {
    if (!username) {
      return;
    }
    this.privateChatSessionData = {
      roomName,
      username,
    };
    const mainChatLightRef = FirebaseService.FirebaseMain.child(
      `chatRooms/${roomName}/privateMessages/${username.toLowerCase()}`
    );
    ChatService.watchPrivateChat(roomName, username, this.getLastPrivateMsgs).then(() => {
      mainChatLightRef.on('value', (snapshot) => {
        const messages = snapshot.val();
        if (messages) {
          const messageKey = snapshot.key();
          const parsedHistoryMessages = Object.keys(snapshot.val()).map(async(messageHash) => {
            if (messages[messageHash].gid) {
              const parsedMsg = await GiphyStore.getSingleGif(
                messages[messageHash],
                messageKey,
                FirebaseStore.kiirooRoom
              );

              return parsedMsg;
            }

            return ChatService.parseHistoryMessage(messageKey, messages[messageHash]);
          });
          Promise.all(parsedHistoryMessages).then((values) => {
            const adminSentMsg = [
              MESSAGE_TYPES.ADMIN,
              MESSAGE_TYPES.MODERATOR,
              MESSAGE_TYPES.USER_MODERATOR,
            ].includes(values[values.length - 1].senderAccessLevel);
            const usernameOfLastMessageSent = values.length > 0 && values[values.length - 1].senderOriginalUsername;
            const isLoggedInUserSentLastMsg = usernameOfLastMessageSent?.toLowerCase() !== username;
            const ifChatIsHidden = this.hiddenChats.includes(usernameOfLastMessageSent);
            if (ifChatIsHidden) {
              this.showChat(usernameOfLastMessageSent);
            }
            if (adminSentMsg && isLoggedInUserSentLastMsg) {
              if (!isBroadcastWindow && this.profileChatTabValue !== 2) {
                this.setProfileChatTabValue(2);
              } else if (isBroadcastWindow) {
                this.setBroadcasterChatTab(TabPanelType.privateChat);
              }
              this.setActiveConversation(values[values.length - 1].senderOriginalUsername);
            }

            this.populateInitialPrivateMessageHistory(values);
          });
        }
      });
    });
  };

  @action watchGroupShowChat = (roomName) => {
    const groupShowChatRef = FirebaseService.FirebaseMain.child(`chatRooms/${roomName}/groupShowMessages`);
    ChatService.watchGroupShowChat(roomName, this.getLastGroupShowMessages).then(() => {
      groupShowChatRef.on('value', (snapshot) => {
        const messages = snapshot.val();
        if (messages) {
          const messageKey = snapshot.key();
          const parsedHistoryMessages = Object.keys(snapshot.val()).map(async(messageHash) => {
            if (messages[messageHash].gid) {
              const parsedMsg = await GiphyStore.getSingleGif(
                messages[messageHash],
                messageKey,
                FirebaseStore.kiirooRoom
              );

              return parsedMsg;
            }

            return ChatService.parseHistoryMessage(messageKey, messages[messageHash]);
          });
          Promise.all(parsedHistoryMessages).then((values) => {
            const duplicate = this.groupShowMessages.find(
              (msg) => msg.timestamp === values[values.length - 1].timestamp
            );
            if (duplicate) {
              return;
            }
            this.addGroupShowMessage(values[values.length - 1]);
          });
        }
      });
    });
  };

  @action getLastMsgs = (snapshot, username) => {
    const messages = snapshot.val();
    if (messages) {
      return this.getLastMessages(snapshot.key(), messages, username);
    }

    return new Promise((resolve) => resolve((this.initialMessagesSet = true)));
  };

  @action getLastMessages(messageKey, messages, username) {
    const parsedHistoryMessages = Object.values(messages).map(async(messageHash) => {
      if (messageHash.gid) {
        const parsedMsg = await GiphyStore.getSingleGif(messageHash, messageKey, FirebaseStore.kiirooRoom);

        return parsedMsg;
      }

      return ChatService.parseHistoryMessage(messageKey, {
        ...messageHash,
        isLiveTouchOn: FirebaseStore.kiirooRoom,
      });
    });

    return Promise.all(parsedHistoryMessages)
      .then((values) => this.populateInitialMessageHistory(values, username));
  }

  @action getLastPrivateMsgs = (snapshot) => {
    const messages = snapshot.val();
    if (messages) {
      const messageKey = snapshot.key();
      const parsedHistoryMessages = Object.keys(snapshot.val()).map(async(messageHash) => {
        if (messages[messageHash].gid) {
          const parsedMsg = await GiphyStore.getSingleGif(messages[messageHash], messageKey, FirebaseStore.kiirooRoom);

          return parsedMsg;
        }

        return ChatService.parseHistoryMessage(messageKey, messages[messageHash]);
      });
      Promise.all(parsedHistoryMessages).then((values) => {
        this.populateInitialPrivateMessageHistory(values);
      });
    }
  };

  @action addEmoji = (e) => {
    this.chatInputValue = this.chatInputValue.concat(e.native);
  };

  @action getChatSettings = (username, isPerformerUsername) => {
    let settingRequest = Promise.resolve(isPerformerUsername ? this.chatSettingsPerformer : this.chatSettings);
    if (username) {
      settingRequest = ChatService.getChatSettings(username).then((response) => response.data);
    }
    settingRequest
      .then((settings) => window?.abt?.chat?.computeChatSettings?.(settings) || settings)
      .then((settings) => {
        if (isPerformerUsername) {
          this.chatSettingsPerformer = settings;
        } else {
          this.chatSettings = settings;
          this.messagingService.emit(UPDATE_ANONYMOUS_TIPPING_HASH, settings.stealthModeValue);
        }
      });
  };

  @action _toggleChatFilters = (settingsToChange, filterToChange, username, accessHash, roomName) => {
    if (!Object.hasOwn(filterToChange, 'stealthModeValue')) {
      // [TODO] - IM-2667  Someone fix the  stealthModeValue.split issue
      // Found when solving IM-2655 , caused by IM-2016, caused probably by BE slow response.
      // this.chatSettings isn't populated at the time and stealthModeValue is non existent
      this[settingsToChange] = {
        ...this[settingsToChange],
        ...filterToChange,
        username,
        accessHash,
        roomName,
        stealthModeValue: this[settingsToChange].stealthModeValue.split('|')[0],
      };
    } else {
      this[settingsToChange] = { ...this[settingsToChange], ...filterToChange, username, accessHash, roomName };
    }

    if (
      Object.hasOwn(filterToChange, 'allowPrivateMessagesFromOtherViewers') &&
      !filterToChange.allowPrivateMessagesFromOtherViewers
    ) {
      this.resetActiveConversation();
    }

    ChatService.changeChatSettings(this[settingsToChange]);
  };

  @action togglePerformerChatFilters = (filterToChange, username, accessHash) => {
    this._toggleChatFilters('chatSettingsPerformer', filterToChange, username, accessHash, username);
  };

  @action toggleChatFilters = (filterToChange, username, accessHash, roomName) => {
    this._toggleChatFilters('chatSettings', filterToChange, username, accessHash, roomName);
  };

  @action saveTranslationSettings = (username, data, isBroadcasterChat) => {
    ChatService.saveTranslationSettings(username, data)
      .then(() => {
        this.messagingService.emit(CHAT_TRANSLATION_SETTINGS_UPDATED, {
          oldData: { ...this.translationSettings },
          newData: { ...data },
        });
        this.translationSettings.active = data.active;
        this.translationSettings.languageCode = data.languageCode;
        this.translationSettings.showOriginalLanguage = data.showOriginalLanguage;
        if (this.isGold || isBroadcasterChat || window?.abt?.chat?.enforceTranslations) {
          this.translateAll();
          this.translatePrivateMsgs();
        }
        TrackingService.track({
          event: 'cam4Translator',
          eventCategory: 'cam4Translator',
          eventAction: data.active ? 'translatorOn' : 'translatorOff',
          eventLabel: translationLanguages[data.languageCode],
        });
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.changesSaved'),
          subtype: NotificationService.subtype.SUCCESS,
        });
      })
      .catch((error) => {
        parseCatch(error);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.errorGeneric'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };

  @action setLanguage = (lang) => {
    const hasChanged = this.translationSettings.languageCode !== lang;
    this.translationSettings.languageCode = lang;
    if (hasChanged && this.translationSettings.active) {
      this.messages.forEach((msg) => {
        msg.translatedMessage = undefined;
      });
      this.privateMessages.forEach((msg) => {
        msg.translatedMessage = undefined;
      });
      this.translateAll();
      this.translatePrivateMsgs();
    }
  };

  @action resetTranslationSettings = () => {
    Promise.resolve({})
      .then((s) => window?.abt?.chat?.computeTranslationSettings?.(s) || s)
      .then((s) => {
        runInAction(() => {
          this.translationSettings.active = s.active || false;
        });
      });
  };

  @action turnOffRoomInvites = (data) => {
    this.chatSettings = {
      ...this.chatSettings,
      ...{ turnOffRoomInvites: true },
      ...data,
      stealthModeValue: this.chatSettings.stealthModeValue.split('|')[0],
    };
    ChatService.changeChatSettings(this.chatSettings);
  };

  @action disableChatFromOtherBroadcasters = (data) => {
    this.chatSettings = {
      ...this.chatSettings,
      ...{ disableChatFromOtherBroadcasters: true },
      ...data,
      stealthModeValue: this.chatSettings.stealthModeValue.split('|')[0],
    };
    ChatService.changeChatSettings(this.chatSettings);
  };

  @action setRegularFontGuest = (data) => {
    this.chatSettings = { ...this.chatSettings, ...{ normalScreenFontSize: 16 }, ...data };
    ChatService.saveChatSettings(this.chatSettings);
  };

  translateAll = async() => {
    await Promise.allSettled(this.messages.map((msg) => this._translateAndSaveMessage(msg)));
    this.messages = [...this.messages];

    if (this.groupShowMessages.length) {
      await Promise.allSettled(this.groupShowMessages.map((msg) => this._translateAndSaveMessage(msg)));
    }
    this.messagingService.emit(INITIAL_MESSAGE_TRANSLATION);
  };

  _translateAndSaveMessage = async(msg) => {
    if (msg.type !== NOTIFICATION_TYPE.MESSAGE || msg.translatedMessage !== undefined || msg.message === '') {
      return;
    }

    const translatedMsg = await ChatService.translateMsg(msg, this.translationSettings);
    this._saveTranslatedMessage(msg, translatedMsg);
  };

  _saveTranslatedMessage = (msg, translatedMsg) => {
    const lang = this.translationSettings.languageCode;
    const isSame = msg.message === translatedMsg.translatedText;
    if (lang !== translatedMsg.detectedSourceLanguage && !isSame) {
      extendObservable(msg, {
        translatedMessage: translatedMsg.translatedText,
        detectedSourceLanguage: translatedMsg.detectedSourceLanguage,
      });
    } else {
      extendObservable(msg, { translatedMessage: '', detectedSourceLanguage: translatedMsg.detectedSourceLanguage });
    }
  };

  translatePrivateMsgs = async() => {
    const lang = this.translationSettings.languageCode;

    if (
      this.translationSettings.active &&
      (this.isGold || isBroadcastWindow || window?.abt?.chat?.enforceTranslations)
    ) {
      await this.privateMessages.map((msg) => {
        if (
          msg?.senderOriginalUsername !== UserStore?.user?.username &&
          msg.type === NOTIFICATION_TYPE.MESSAGE &&
          (msg.translatedMessage === undefined && msg.message !== '')
        ) {
          ChatService.translateMsg(msg, this.translationSettings).then((res) => {
            const isSame = msg.message === res.translatedText;
            let translatedMessage = '';
            if (lang !== res.detectedSourceLanguage && !isSame) {
              translatedMessage = res.translatedText;
            }
            runInAction(() => {
              msg.translatedMessage = translatedMessage;
              msg.detectedSourceLanguage = res.detectedSourceLanguage;
            });
          });
        }

        return { translated: true };
      });
      this.messagingService.emit(INITIAL_MESSAGE_TRANSLATION);
    }
  };

  getTranslationSettings = (username) => {
    const mainLanguage = ProfileStore.loggedInUserInfo?.mainLanguage;
    ChatService.getTranslationSettings(username, mainLanguage).then((response) => {
      this.translationSettings = response;
      if (this.translationSettings.active) {
        this.translateAll();
        this.translatePrivateMsgs();
      }
    });
  };

  banUserFromChatIfBroadcaster = (username, accessLevel, roomName) => {
    if (!username) {
      return false;
    }
    const isFromExcludedGroup =
      username.toLowerCase() === ProfileStore.profileName.toLowerCase() ||
      username.toLowerCase() === roomName.toLowerCase() ||
      ProfileStore.performerFriendStatus !== 'NONE' ||
      FirebaseStore.isLoggedInUserRoomModerator(username) ||
      AccessLevelService.isMin(verifiedRoomModerator, accessLevel);

    if (
      this.chatSettingsPerformer.disableChatFromOtherBroadcasters &&
      FirebaseStore.isLoggedInUserBroadcasting(username) &&
      !isFromExcludedGroup
    ) {
      this.handleChatInputError(true);
      this.setChatInputError(SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT);

      return true;
    }
    this.handleChatInputError(false);

    return false;
  };

  @action getLastGroupShowMessages = (snapshot) => {
    const messages = snapshot.val();
    if (messages) {
      const messageKey = snapshot.key();
      const parsedHistoryMessages = Object.keys(snapshot.val()).map(async(messageHash) => {
        if (messages[messageHash].gid) {
          const parsedMsg = await GiphyStore.getSingleGif(messages[messageHash], messageKey, FirebaseStore.kiirooRoom);

          return parsedMsg;
        }

        return ChatService.parseHistoryMessage(messageKey, messages[messageHash]);
      });
      Promise.all(parsedHistoryMessages).then((values) => {
        this.populateInitialGroupShowMessages(values);
      });
    }
  };

  @action sendGroupShowMessage = (username, accessHash, roomname, message, isGif, gifUrlAnalytics) => {
    if (!message) {
      return;
    }
    if (!message.trim()) {
      this.handleChatInputError(true);
      this.setChatInputError(SENT_CHAT_MESSAGE_STATUSES.EMPTY_MESSAGE);
    } else {
      ChatService.sendGroupShowMessage(username, accessHash, roomname, message, isGif, gifUrlAnalytics).then((res) => {
        const isErrorVisible = [
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MESSAGE_SPAM,
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_TOO_MANY_MESSAGES,
          SENT_CHAT_MESSAGE_STATUSES.THROTTLER_MUST_WAIT,
          SENT_CHAT_MESSAGE_STATUSES.SPAM_DETECTED,
          SENT_CHAT_MESSAGE_STATUSES.BROADCASTERS_NOT_ALLOWED_TO_CHAT,
        ].includes(res.data.status);
        if (isErrorVisible) {
          this.handleChatInputError(true);
          this.setChatInputError(res.data.status);
        }
      });
    }
    this.chatInputValue = '';
  };

  @action deleteChat = (key) => {
    if (!this.hiddenChats.includes[key]) {
      this.hiddenChats.push(key);
    }
  };

  @action showChat = (key) => {
    this.hiddenChats = this.hiddenChats.filter((item) => item !== key);
  };

  @computed get privateUserChats() {
    let isPrivateMessagesEnabled = this.chatSettings.allowPrivateMessagesFromOtherViewers;
    if (this.isBwr) {
      isPrivateMessagesEnabled = !this.chatSettingsPerformer.disablePrivateChat;
    }
    const conversationStructure = {};
    this.privateMessages.forEach((message) => {
      const isSameUser = this.privateChatSessionData.username === message.senderOriginalUsername;
      const isPrvChatMsgFromSilencedUser =
        ProfileStore.silenceList.totalUsersCount > 0 &&
        ProfileStore.silencedListUsernames?.includes(message.senderOriginalUsername) &&
        !isSameUser;

      let key = message.recipient;
      if (this.privateChatSessionData.username === message.recipient.toLowerCase()) {
        key = message.senderOriginalUsername;
      }
      if (!conversationStructure[key]) {
        conversationStructure[key] = {
          lastMessageTimestamp: null,
          messages: [],
          accessLevel:
            key.toLowerCase() === message.senderOriginalUsername.toLowerCase() ? message.senderAccessLevel : null,
        };
      }
      if (isPrvChatMsgFromSilencedUser) {
        const usr = ProfileStore.silenceList.usersList.find((item) => item.username === key);
        if (moment(usr.date).format('x') > message.timestamp) {
          conversationStructure[key].messages.push(message);
        }
      } else {
        conversationStructure[key].messages.push(message);
      }
      if (
        !conversationStructure[key].lastMessageTimestamp ||
        conversationStructure[key].lastMessageTimestamp < message.timestamp
      ) {
        conversationStructure[key].lastMessageTimestamp = message.timestamp;
      }
    });

    if (this.activeConversation && !conversationStructure[this.activeConversation]) {
      conversationStructure[this.activeConversation] = {
        lastMessageTimestamp: null,
        messages: [],
      };
    }
    if (isPrivateMessagesEnabled) {
      return conversationStructure;
    }
    let adminNamesOrBroadcaster = Object.keys(conversationStructure);
    if (FirebaseStore.newShowsState === NEW_SHOWS_STATE.INSIDE_PS && this.isBwr) {
      adminNamesOrBroadcaster = adminNamesOrBroadcaster
        .filter(
          (conversation) =>
            ['admin', 'userModerator', 'moderator'].includes(conversationStructure[conversation].accessLevel) ||
              conversation.toLowerCase() === ProfileStore.profileName.toLowerCase() ||
              conversation.toLowerCase() ===
                BroadcasterPrivateShowStore.originalUsernameInsidePrivateShow?.toLowerCase()
        )
        .map((item) => item.toLowerCase());
    } else {
      adminNamesOrBroadcaster = adminNamesOrBroadcaster
        .filter(
          (conversation) =>
            ['admin', 'userModerator', 'moderator'].includes(conversationStructure[conversation].accessLevel) ||
              conversation.toLowerCase() === ProfileStore.profileName.toLowerCase()
        )
        .map((item) => item.toLowerCase());
    }
    const filteredStructureIfChatIsDisabled = Object.keys(conversationStructure)
      .filter((key) => adminNamesOrBroadcaster.includes(key.toLowerCase()))
      .reduce((obj, key) => {
        return { ...obj, [key]: conversationStructure[key] };
      }, {});

    return filteredStructureIfChatIsDisabled;
  }

  @action startConversation = (user) => {
    if (!has(this.conversations, user.originalUsername)) {
      set(this.conversations, { [user.originalUsername]: { ...user, messages: [] } });
    }
    this.hiddenChats = this.hiddenChats.filter((item) => item !== user.originalUsername);
    this.setActiveConversation(user.originalUsername);
  };

  @action setActiveConversation = (username) => {
    this.activeConversation = username;
    set(this.activeReadConversations, { [username]: { lastReadTimestamp: Date.now() } });
  };

  @action resetActiveConversation = () => {
    this.activeConversation = null;
  };

  @action clearMessages = () => {
    this.messages = [];
    this.privateMessages = [];
    this.privateChats = {};
  };

  @computed get activeConversationNames() {
    let isPrivateMessagesEnabled = this.chatSettings.allowPrivateMessagesFromOtherViewers;
    if (this.isBwr) {
      isPrivateMessagesEnabled = !this.chatSettingsPerformer.disablePrivateChat;
    }

    return Object.keys(this.privateUserChats)
      .filter((key) => {
        const validConversation =
          key.length > 0 &&
          key &&
          key.toLowerCase() !== ProfileStore.profileName.toLowerCase() &&
          !this.hiddenChats.includes(key);
        const adminOrModerator =
          ['admin', 'userModerator', 'moderator'].includes(this.privateUserChats[key].accessLevel);

        if (isPrivateMessagesEnabled) {
          return validConversation;
        }

        return validConversation && adminOrModerator;
      })
      .sort((a, b) => this.privateUserChats[b].lastMessageTimestamp - this.privateUserChats[a].lastMessageTimestamp);
  }

  @computed get isTherePrivateChatWithAdmin() {
    return Object.keys(this.privateUserChats).find((element) =>
      [MESSAGE_TYPES.ADMIN, MESSAGE_TYPES.MODERATOR, MESSAGE_TYPES.USER_MODERATOR].includes(
        this.privateUserChats[element].accessLevel
      )
    );
  }


  @action setPrivateShowVisibilityChat = (isPrivateShowChatShown) => {
    this.isPrivateShowChatShown = isPrivateShowChatShown;
  };

  @action setProfileChatTabValue = (value) => {
    this.profileChatTabValue = value;
  };

  @computed get isMobileKeyboardShown() {
    return this.isMobileKeyboardActive && this.initialWindowHeight > this.windowHeight;
  }

  filterGroupShowMessages = (messages) => {
    if (FirebaseStore.newShowsDetails?.gsTime) {
      if (FirebaseStore.newShowsState === NEW_SHOWS_STATE.BONUS_GS) {
        return messages.filter(
          (msg) => FirebaseStore.newShowsDetails.currentStart - FirebaseStore.newShowsDetails.gsTime < msg.timestamp
        );
      }

      return messages.filter((msg) => FirebaseStore.newShowsDetails.currentStart < msg.timestamp);
    }

    return null;
  };

  @computed get groupShowMessagesAndNotifications() {
    if (this.messages.length) {
      const filteredGroupShowOnlyMessages = this.filterGroupShowMessages(this.groupShowMessages) || [];
      const mixedGroupShowMessagesWithPublicNotifs = this.messages
        .filter((msg) => {
          if (msg.timestamp && FirebaseStore.newShowsDetails?.gsTime) {
            if (FirebaseStore.newShowsState === NEW_SHOWS_STATE.BONUS_GS) {
              // gets the start of gs and doesn't omit notifs from group show
              return (
                FirebaseStore.newShowsDetails.currentStart - FirebaseStore.newShowsDetails.gsTime < msg.timestamp &&
                [
                  NOTIFICATION_TYPE.SEND_TIP,
                  NOTIFICATION_TYPE.GIFT,
                  NOTIFICATION_TYPE.GAME,
                  NOTIFICATION_TYPE.LIVE_TOUCH,
                ].includes(msg.type)
              );
            }

            // gets the start of gs and omits the notification before gs
            return (
              FirebaseStore.newShowsDetails.currentStart < msg.timestamp &&
              [
                NOTIFICATION_TYPE.SEND_TIP,
                NOTIFICATION_TYPE.GIFT,
                NOTIFICATION_TYPE.GAME,
                NOTIFICATION_TYPE.LIVE_TOUCH,
              ].includes(msg.type)
            );
          }

          return false;
        })
        .concat(...filteredGroupShowOnlyMessages)
        .sort((a, b) => a.timestamp - b.timestamp);
      const checkIfLastPublicMessageIsGame =
        mixedGroupShowMessagesWithPublicNotifs.length &&
        mixedGroupShowMessagesWithPublicNotifs[0].type === NOTIFICATION &&
        mixedGroupShowMessagesWithPublicNotifs[0].notificationExtra;
      const isLastMessageGameOver =
        checkIfLastPublicMessageIsGame && Object.keys(checkIfLastPublicMessageIsGame).length > 0;
      if (isLastMessageGameOver) {
        return mixedGroupShowMessagesWithPublicNotifs.length && mixedGroupShowMessagesWithPublicNotifs.slice(1);
      }

      return mixedGroupShowMessagesWithPublicNotifs;
    }

    return this.filterGroupShowMessages(this.groupShowMessages);
  }

  @computed get liveTouchMessageAdded() {
    return this.messages.find((msg) => msg.type === NOTIFICATION_TYPE.LIVE_TOUCH);
  }

  @computed get campaignMsg() {
    return this.messages.some((msg) => msg.type === NOTIFICATION_TYPE.CAMPAIGN_CTA);
  }

  @action _handleLogout = () => {
    this.setProfileChatTabValue(0);
    this.activeConversation = null;
  };

  @action resetChatStoreState = () => {
    Object.keys(chatStoreInitialState).forEach((item) => (this[item] = chatStoreInitialState[item]));
  };

  @action
    _handleFirebaseUnavailable = () => {
      const firebaseUnavailableMessage = {
        message: translate('v2profile.firebase.unavailable.warning.body'),
        senderAccessLevel: MESSAGE_TYPES.ADMIN,
        isSilenced: false,
        senderOriginalUsername: '',
        type: 1,
        timestamp: Date.now(),
      };
      this.messages.push(firebaseUnavailableMessage);
    };

  _handleModNotification = (data) => {
    this.addMessage({
      type: NOTIFICATION_TYPE.ROOM_MODERATOR_PRIVILEGES,
      senderOriginalUsername: data.username,
      messageKey: `${data.username}${data.isRoomModerator}`,
      isRoomModerator: data.isRoomModerator,
    });
  };

  @action resetChatSettings = () => {
    this.chatSettings = { stealthModeValue: 'visible' };
    this.translationSettings = {
      active: false,
      languageCode: null,
      showOriginalLanguage: false,
    };
  };

  @action setLastToastTippingTimestamp = (value) => {
    this.lastTippingToastNotificationTimestamp = value;
  };

  haveMessagesWithPerformer = (username) =>
    computed(() => this.privateUserChats[username]?.messages.length > 0).get();

  @computed get tipMenuNotificationPresent() {
    const numOfLastMessages = -3;

    return this.messages
      .slice(numOfLastMessages)
      .find((msg) => msg.gameName === GAMES.TIP_MENU && JSON.parse(msg.notificationExtra).s === GAMES_STATUS.IDLE);
  }

  @computed get onlyNotificationsInChat() {
    return this.messages.filter(
      (msg) => [
        NOTIFICATION_TYPE.SEND_TIP,
        NOTIFICATION_TYPE.GIFT,
        NOTIFICATION_TYPE.JOIN_FANCLUB,
        NOTIFICATION_TYPE.MARKETPLACE_PURCHASE,
        NOTIFICATION_TYPE.MARKETPLACE_BULK_PURCHASE,
      ].includes(msg.type) ||
        (msg.type === NOTIFICATION_TYPE.GAME && JSON.parse(msg.notificationExtra).s !== GAMES_STATUS.IDLE)
    );
  }

  @computed get onlyTippingNotifs() {
    return this.messages.filter((msg) => msg.type === NOTIFICATION_TYPE.SEND_TIP);
  }

  @action setScrollAtBottom = (value) => {
    // If the scroll is at the bottom, mark active conversation as read
    if (value && !this.isScrollAtBottom) {
      const tabIndex = this.isBwr ? this.activeBroadcasterChatTab : this.profileChatTabValue;

      if (tabIndex === TabPanelType.privateChat && this.activeConversation) {
        set(this.activeReadConversations, { [this.activeConversation]: { lastReadTimestamp: Date.now() } });
      }
      if (tabIndex === TabPanelType.specialShowChat) {
        this.hasUnreadGsMessages = false;
      }
    }
    this.isScrollAtBottom = value;
  };
}

const chatStore = new ChatStore();

reaction(() => chatStore.privateMessages, (privateMessages) => {
  if (chatStore.chatSettingsPerformer?.privateMessageSound) {
    const latestMessage = privateMessages[privateMessages.length - 1];
    BroadcastNotificationsService.handlePrivateMessageNotificationSound(latestMessage);
  }
});

export default chatStore;
